<template>
  <SrpModal v-model:isVisible="isVisibleProxy" size="large" isWithScroll :isClosable="false" max-height="calc(100dvh - 200px)">
    <template #header>
      <div class="alt-modal__header-row">
        <h2 class="global-h2 alt-modal__title">Image Accessibility Text</h2>
        <SrpCheckbox :isWithHalo="false" whiteSpace="nowrap" style="margin-right: 20px !important">
          <template #input><input type="checkbox" v-model="onlyEmptyFilter" /></template>
          <template #text>Empty rows</template>
        </SrpCheckbox>
      </div>
    </template>
    <template #content>
      <p class="alt-modal__hint">Add a short description for accessibility purposes (ex. screen readers use this).</p>
      <div v-if="loadError" class="alt-modal__error">{{ loadError }}</div>
      <div ref="altRowsRef" class="alt-modal__rows">
        <div v-for="(photoId, index) in displayedPhotoIds" :key="photoId" class="alt-modal__row">
          <div class="alt-modal__thumb-wrap">
            <SrpFileThumbnail
              :file-name="photoId"
              thumb-size="thumb-tiny"
              image-fit="contain"
              :is-with-loader="false"
              :is-entire-area-clickable="true"
              galleryPostfix="-alt-modal"
              preloader-min-width="120px"
            />
          </div>
          <textarea v-model="draft[photoId]" class="global-textarea alt-modal__textarea" rows="2" :tabindex="index + 1" :aria-label="`Alt text for ${photoId}`" />
        </div>
      </div>
    </template>
    <template #footer>
      <SrpButton color="gray" fill="outlined" @click="onCancel" :isDisabled="isSaving">Cancel</SrpButton>
      <SrpButton @click="onSave" :isDisabled="isSaving || isLoading">{{ isSaving ? "Saving…" : "Save" }}</SrpButton>
    </template>
  </SrpModal>
</template>

<script setup lang="ts">
import axios from "axios";
import { computed, nextTick, ref, watch } from "vue";
import type { PhotoWidgetOtherDataResponse, PhotoWidgetOtherDataSaveRequest } from "@contracts/photoWidget";
import SrpCheckbox from "@components/ui/SrpCheckbox.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import SrpFileThumbnail from "@components/ui/SrpFileThumbnail.vue";
import SrpModal from "@components/ui/SrpModal.vue";

const props = withDefaults(
  defineProps<{
    isVisible: boolean;
    customerId: string | null;
    widgetId: string | null;
    photoIds: string[];
  }>(),
  {
    customerId: null,
    widgetId: null,
    photoIds: () => [],
  }
);

const emit = defineEmits<{
  (e: "update:isVisible", value: boolean): void;
}>();

const isVisibleProxy = computed({
  get: () => props.isVisible,
  set: (v: boolean) => emit("update:isVisible", v),
});

const draft = ref<Record<string, string>>({});
const onlyEmptyFilter = ref(false);
const displayedPhotoIds = computed(() => {
  if (!onlyEmptyFilter.value) return props.photoIds;
  return props.photoIds.filter(id => !(draft.value[id] ?? "").trim());
});

const altRowsRef = ref<HTMLElement | null>(null);
const isLoading = ref(false);
const isSaving = ref(false);
const loadError = ref<string | null>(null);

watch(
  () => props.isVisible,
  async visible => {
    if (!visible || !props.customerId || !props.widgetId) return;
    onlyEmptyFilter.value = false;
    loadError.value = null;
    isLoading.value = true;
    try {
      const { data } = await axios.get<PhotoWidgetOtherDataResponse>(`${import.meta.env.VITE_API_URL}/photo-widget-admin/${props.customerId}/widgets/${props.widgetId}/other-data`);
      const next: Record<string, string> = {};
      const map = data?.photoAltByPhotoId ?? {};
      for (const id of props.photoIds) {
        next[id] = map[id] ?? "";
      }
      draft.value = next;
    } catch (e) {
      loadError.value = "Could not load alt text. Try again.";
      console.error(e);
      const next: Record<string, string> = {};
      for (const id of props.photoIds) next[id] = "";
      draft.value = next;
    } finally {
      isLoading.value = false;
      if (displayedPhotoIds.value.length > 0) {
        await nextTick();
        await nextTick();
        altRowsRef.value?.querySelector<HTMLTextAreaElement>("textarea")?.focus();
      }
    }
  }
);

function onCancel(): void {
  emit("update:isVisible", false);
}

async function onSave(): Promise<void> {
  if (!props.customerId || !props.widgetId) return;
  isSaving.value = true;
  loadError.value = null;
  try {
    const photoAltByPhotoId: Record<string, string> = {};
    for (const id of props.photoIds) {
      const t = (draft.value[id] ?? "").trim();
      if (t.length > 0) photoAltByPhotoId[id] = t;
    }
    const body: PhotoWidgetOtherDataSaveRequest = { photoAltByPhotoId };
    await axios.put(`${import.meta.env.VITE_API_URL}/photo-widget-admin/${props.customerId}/widgets/${props.widgetId}/other-data`, body, { headers: { "Content-Type": "application/json" } });
    emit("update:isVisible", false);
  } catch (e) {
    loadError.value = "Save failed. Try again.";
    console.error(e);
  } finally {
    isSaving.value = false;
  }
}
</script>

<style scoped lang="scss">
.alt-modal__header-row {
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px 20px;
}

.alt-modal__title {
  margin: 0;
}

.alt-modal__hint {
  margin: 0 0 16px;
  color: rgba(91, 91, 91, 1);
  font: 14px/18px sans-serif;
}

.alt-modal__error {
  margin-bottom: 12px;
  color: #c0392b;
  font-size: 14px;
}

.alt-modal__rows {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.alt-modal__row {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 14px;
}

.alt-modal__thumb-wrap {
  flex-shrink: 0;
  width: 120px;

  :deep(.file-thumbnail) {
    width: 100%;
  }
}

.alt-modal__textarea {
  flex: 1;
  min-width: 0;
  resize: vertical;
  min-height: 52px;
  height: auto;
  line-height: 1.35;
}
</style>
